import { RefObject } from 'react'

import { Box, Tag } from '@factorialco/deprecated-design-system'
import { BACKEND_DATETIME_FORMAT } from 'config/Vars'
import {
  AtsConversationApplicationFragment,
  AtsConversationMessageFragment,
  AtsMessageBoardConversationFragment,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import buildUrl from 'modules/core/lib/buildUrl'
import { FORMAT } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DelayedMessageCard } from 'modules/ats/components/MessageBoard/DelayedMessageCard'
import { MessageCard } from 'modules/ats/components/MessageBoard/MessageCard'
import { isMessageDelayed } from 'modules/ats/lib/utils/messages'

interface Sender {
  fullName: string
  avatarUrl: string | null
}

const getSender = (
  message: AtsConversationMessageFragment,
  application: AtsConversationApplicationFragment
): Sender => {
  const senderType = message.sentByType
  switch (senderType) {
    case 'User': {
      return {
        fullName: message.access
          ? message.access.fullName
          : i18n.t('ats.candidates.messages.list.unknown_sender'),
        avatarUrl: message.access?.avatar ?? null,
      }
    }
    case 'Ats::Candidate':
      return {
        fullName: application.candidate.fullName,
        avatarUrl: buildUrl(application.photo),
      }
    default:
      throw new Error(`Sender type ${senderType} not valid`)
  }
}

interface Props {
  conversation: AtsMessageBoardConversationFragment
  messages: AtsConversationMessageFragment[]
  scrollRef: RefObject<HTMLElement>
}

export const MessagesList = ({ conversation, messages, scrollRef }: Props) => {
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, messages.length])

  const messagesGroupedByDate = groupBy(messages, (message) => {
    return moment(message.createdAt, BACKEND_DATETIME_FORMAT).format(FORMAT.ui_long_with_year)
  })

  return (
    <Box component="ul" paddingTop="s12" gap="s32">
      {map(messagesGroupedByDate, (group, date) => (
        <Box key={date} component="li" gap="s24" alignItems="center">
          <Tag type="grey">{date}</Tag>
          <Box component="ul" gap="s32" width="full">
            {group.map((message) => (
              <Box key={message.id} component="li">
                {isMessageDelayed(message) ? (
                  <DelayedMessageCard message={message} />
                ) : (
                  <MessageCard
                    sender={getSender(message, conversation.application)}
                    message={message}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
